import React, { useState } from "react";
import { X } from "lucide-react";
import { useT, useLanguage } from "../lib/LanguageContext";
import { postDisplayLanguage } from "../lib/blogLanguage";
import MarkdownBody from "../components/MarkdownBody";
import { completeLanguages } from "./blogPayload";

/**
 * A full post rendered the way the public article shows it (same Markdown renderer), so a
 * draft can be read before it is published. One button per complete language.
 *
 * @param {{post: object, onClose: () => void}} props `post` is the FULL post
 *   (`GET /super-admin/hq/blog/{post_id}`), drafts included.
 */
export default function HqBlogPreview({ post, onClose }) {
  const t = useT("superadmin");
  const { lang: uiLang } = useLanguage();
  const langs = completeLanguages(post);
  // Opens on the language a reader with the operator's UI language would be shown.
  const [lang, setLang] = useState(() => postDisplayLanguage({ languages: langs }, uiLang));
  // Every field of the post is read in ONE language; a stray field of an unfinished one is
  // never shown.
  const view = Object.fromEntries(["title", "excerpt", "tag", "body"].map((f) => [f, post[`${f}_${lang}`] || ""]));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white border-l border-slate-200 overflow-y-auto" data-testid="blog-preview">
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="font-sans text-2xl text-slate-800">{t("hqblog.preview")}</div>
            {langs.map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={`text-xs font-semibold rounded px-2 py-1 ${l === lang ? "bg-sol-gold text-black" : "bg-slate-100 text-slate-600"}`}
                data-testid={`blog-preview-lang-${l}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} aria-label={t("hqblog.close")}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <article className="p-8">
          {!post.published && <div className="text-xs uppercase tracking-wider text-amber-600 mb-3">{t("hqblog.draft")}</div>}
          {view.tag && <div className="text-xs uppercase tracking-wider text-amber-600 mb-3">{view.tag}</div>}
          <h1 className="font-sans font-semibold text-4xl mb-3 text-slate-800">{view.title}</h1>
          {view.excerpt && <p className="text-slate-500 text-lg mb-6">{view.excerpt}</p>}
          {post.cover_url && <img src={post.cover_url} alt={view.title} className="w-full rounded-2xl mb-8" />}
          <MarkdownBody source={view.body} tone="light" testId="blog-preview-body" />
        </article>
      </div>
    </div>
  );
}
